import html2canvas from 'html2canvas';
import PptxGenJS from 'pptxgenjs';
import jsPDF from 'jspdf';
import { ChartData, AIInsight } from '@/types/dashboard';

/**
 * Yield to the main thread to keep UI responsive.
 * Uses requestIdleCallback if available, otherwise setTimeout.
 */
function yieldToMain(): Promise<void> {
  return new Promise((resolve) => {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => resolve(), { timeout: 50 });
    } else {
      setTimeout(resolve, 0);
    }
  });
}

/**
 * Copy chart as image to clipboard with chart title on top.
 * Optimized for smooth UI - uses chunked processing and yields to main thread.
 */
export async function copyChartAsImage(
  chartElement: HTMLElement,
  chartTitle: string,
  options?: {
    /**
     * Render scale multiplier for html2canvas. Higher = sharper text, larger image.
     * Defaults to 2 for good quality without excessive processing.
     */
    scale?: number;
    /**
     * Skip scrollIntoView (if caller already handled visibility).
     */
    skipScroll?: boolean;
  },
): Promise<boolean> {
  try {
    console.log('[chartExport] Starting chart copy with optimized approach');
    
    // Yield to allow UI to update (show loader)
    await yieldToMain();

    // Only scroll if not skipped by caller
    if (!options?.skipScroll) {
      // Check if element is already visible
      const rect = chartElement.getBoundingClientRect();
      const isVisible = (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );

      if (!isVisible) {
        chartElement.scrollIntoView({ behavior: 'instant', block: 'nearest' });
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Yield again before heavy html2canvas work
    await yieldToMain();
    
    // Use a moderate scale - 2 is good balance of quality vs performance
    // Higher scales cause exponentially more work
    const scale = options?.scale ?? 2;

    console.log('[chartExport] Capturing chart with html2canvas, scale:', scale);

    // Capture the chart - this is the heavy operation
    // html2canvas options optimized for performance:
    // - Lower scale reduces pixel count significantly
    // - logging: false reduces console overhead
    // - removeContainer: true cleans up immediately
    const canvas = await html2canvas(chartElement, {
      backgroundColor: '#ffffff',
      scale,
      logging: false,
      useCORS: true,
      allowTaint: false,
      removeContainer: true,
      // Optimize by ignoring elements that don't affect visual output
      ignoreElements: (element) => {
        // Skip hidden elements and script/style tags
        const tagName = element.tagName?.toLowerCase();
        if (tagName === 'script' || tagName === 'style' || tagName === 'noscript') {
          return true;
        }
        return false;
      },
    });

    // Yield after heavy capture to let UI breathe
    await yieldToMain();

    console.log('[chartExport] Chart captured, creating final image with title');

    // Create a new canvas with title on top
    const titleHeight = 60;
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = canvas.width;
    finalCanvas.height = canvas.height + titleHeight;
    const ctx = finalCanvas.getContext('2d');
    
    if (!ctx) {
      console.error('[chartExport] Failed to get canvas context');
      return false;
    }

    // Fill background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

    // Draw title
    ctx.fillStyle = '#1F2937';
    ctx.font = 'bold 32px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Draw title with word wrapping if needed
    const maxWidth = finalCanvas.width - 40;
    const words = chartTitle.split(' ');
    let line = '';
    let y = titleHeight / 2;
    const lineHeight = 36;
    
    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + ' ';
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && i > 0) {
        ctx.fillText(line.trim(), finalCanvas.width / 2, y);
        line = words[i] + ' ';
        y += lineHeight;
      } else {
        line = testLine;
      }
    }
    if (line.trim()) {
      ctx.fillText(line.trim(), finalCanvas.width / 2, y);
    }

    // Draw the original chart below the title
    ctx.drawImage(canvas, 0, titleHeight);

    // Yield before blob creation
    await yieldToMain();

    // Convert final canvas to blob
    return new Promise((resolve) => {
      finalCanvas.toBlob(async (blob) => {
        if (!blob) {
          console.error('[chartExport] Failed to create blob from canvas');
          resolve(false);
          return;
        }

        try {
          if (!navigator.clipboard || !navigator.clipboard.write) {
            throw new Error('Clipboard API not available.');
          }

          if (typeof ClipboardItem === 'undefined') {
            throw new Error('ClipboardItem API not supported.');
          }

          // Copy to clipboard
          await navigator.clipboard.write([
            new ClipboardItem({
              'image/png': blob,
            }),
          ]);
          console.log(`[chartExport] Successfully copied chart "${chartTitle}"`);
          resolve(true);
        } catch (error) {
          console.error('[chartExport] Clipboard failed, downloading instead:', error);
          
          // Fallback: download the image
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${chartTitle.replace(/[^a-z0-9]/gi, '_')}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          resolve(false);
        }
      }, 'image/png');
    });
  } catch (error) {
    console.error('[chartExport] Error copying chart as image:', error);
    return false;
  }
}

/**
 * Export all charts to PowerPoint
 */
export async function exportChartsToPPT(
  charts: ChartData[],
  query?: string,
  insights?: AIInsight[],
  onProgress?: (step: string, current: number, total: number) => void
): Promise<void> {
  try {
    console.log(`[chartExport] Exporting ${charts.length} charts to PowerPoint`);
    
    const totalSteps = charts.length + (insights && insights.length > 0 ? 2 : 1); // +1 for title, +1 for insights if present
    let currentStep = 0;
    
    const pptx = new PptxGenJS();
    pptx.layout = 'LAYOUT_WIDE';
    
    // Slide 1: Financial Data Overview
    onProgress?.('Creating title slide...', ++currentStep, totalSteps);
    const titleSlide = pptx.addSlide();
    titleSlide.addText('Financial Data Analysis', {
      x: 0.5,
      y: 1.5,
      w: 9,
      h: 1,
      fontSize: 48,
      bold: true,
      align: 'center',
      color: '1F3A5F',
    });
    titleSlide.addText('Comprehensive Financial Dashboard Report', {
      x: 0.5,
      y: 2.3,
      w: 9,
      h: 0.6,
      fontSize: 24,
      align: 'center',
      color: '2C7A7B',
    });
    if (query) {
      titleSlide.addText(`Analysis Query: "${query}"`, {
        x: 0.5,
        y: 3.2,
        w: 9,
        h: 0.5,
        fontSize: 16,
        align: 'center',
        color: '666666',
        italic: true,
      });
    }
    titleSlide.addText(`Generated on ${new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })}`, {
      x: 0.5,
      y: 4.5,
      w: 9,
      h: 0.4,
      fontSize: 14,
      align: 'center',
      color: '999999',
    });
    titleSlide.addText('This report contains comprehensive financial analysis including', {
      x: 0.5,
      y: 5.2,
      w: 9,
      h: 0.3,
      fontSize: 14,
      align: 'center',
      color: '666666',
    });
    titleSlide.addText('key insights, metrics, and visualizations of your financial data.', {
      x: 0.5,
      y: 5.6,
      w: 9,
      h: 0.3,
      fontSize: 14,
      align: 'center',
      color: '666666',
    });

    // Slide 2: Insights Summary
    if (insights && insights.length > 0) {
      onProgress?.('Adding insights summary...', ++currentStep, totalSteps);
      const insightsSlide = pptx.addSlide();
      insightsSlide.addText('Executive Summary & Key Insights', {
        x: 0.5,
        y: 0.3,
        w: 9,
        h: 0.6,
        fontSize: 36,
        bold: true,
        align: 'center',
        color: '1F3A5F',
      });

      // Add insights in a formatted list with full text, centered
      let yPos = 1.2;
      let currentSlide = insightsSlide;
      
      insights.forEach((insight, index) => {
        // Calculate space needed for full description
        // Roughly 70-80 chars per line at 12pt font, 8.5 inch width
        const charsPerLine = 75;
        const estimatedLines = Math.ceil(insight.description.length / charsPerLine);
        const descriptionHeight = Math.max(0.3, estimatedLines * 0.18); // ~0.18 inches per line
        const totalSpaceNeeded = 0.4 + descriptionHeight + 0.2; // title + description + spacing
        
        // Check if we need a new slide before adding this insight
        if (yPos + totalSpaceNeeded > 6.8) {
          // Start a new slide if we run out of space
          currentSlide = pptx.addSlide();
          currentSlide.addText('Executive Summary & Key Insights (Continued)', {
            x: 0.5,
            y: 0.3,
            w: 9,
            h: 0.6,
            fontSize: 36,
            bold: true,
            align: 'center',
            color: '1F3A5F',
          });
          yPos = 1.2;
        }
        
        // Add bullet point - centered
        const bulletColor = insight.type === 'warning' ? 'F59E0B' : 
                          insight.type === 'success' ? '10B981' : 
                          insight.type === 'alert' ? 'EF4444' : '3B82F6';
        
        currentSlide.addText(`• ${insight.title}`, {
          x: 0.5,
          y: yPos,
          w: 9,
          h: 0.3,
          fontSize: 16,
          bold: true,
          align: 'center',
          color: bulletColor,
        });
        
        // Add full description without truncation - centered and wrapped
        // Use larger height to accommodate full text
        currentSlide.addText(insight.description, {
          x: 0.5,
          y: yPos + 0.4,
          w: 9,
          h: Math.max(descriptionHeight, 2.0), // Allow up to 2 inches for long text
          fontSize: 12,
          color: '555555',
          align: 'center',
          wrap: true, // Enable text wrapping
        });
        
        // Move yPos based on actual content height
        yPos += 0.4 + Math.max(descriptionHeight, 0.5) + 0.2; // title space + description + spacing
      });
    }

    // Process each chart
    for (let i = 0; i < charts.length; i++) {
      const chart = charts[i];
      onProgress?.(`Processing chart ${i + 1}/${charts.length}: ${chart.title}`, ++currentStep, totalSteps);
      console.log(`[chartExport] Processing chart ${i + 1}/${charts.length}: ${chart.title}`);
      
      // Find the chart element in the DOM
      const chartElement = document.querySelector(`[data-chart-id="chart-${i}"]`) as HTMLElement;
      
      if (!chartElement) {
        console.warn(`[chartExport] Chart element not found for: ${chart.title}`);
        // Add a slide with text instead
        const slide = pptx.addSlide();
        slide.addText(chart.title, {
          x: 0.5,
          y: 1,
          w: 9,
          h: 0.5,
          fontSize: 32,
          bold: true,
        });
        slide.addText('Chart not available', {
          x: 0.5,
          y: 2,
          w: 9,
          h: 0.5,
          fontSize: 18,
          color: '999999',
        });
        continue;
      }

      // Scroll element into view to ensure it's rendered
      chartElement.scrollIntoView({ behavior: 'instant', block: 'center' });
      await new Promise(resolve => setTimeout(resolve, 300));

      // Capture chart as image
      const canvas = await html2canvas(chartElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: false,
      });

      // Convert canvas to base64
      const imageData = canvas.toDataURL('image/png');

      // Add slide with chart
      const slide = pptx.addSlide();
      
      // Add chart title
      slide.addText(chart.title, {
        x: 0.5,
        y: 0.2,
        w: 9,
        h: 0.4,
        fontSize: 24,
        bold: true,
        align: 'center',
      });

      // Add chart image
      slide.addImage({
        data: imageData,
        x: 0.5,
        y: 0.8,
        w: 9,
        h: 5.5,
      });

      // Add data summary if available
      if (chart.data && chart.data.length > 0) {
        const dataText = `Data Points: ${chart.data.length}`;
        slide.addText(dataText, {
          x: 0.5,
          y: 6.5,
          w: 9,
          h: 0.3,
          fontSize: 12,
          align: 'center',
          color: '666666',
        });
      }
    }

    // Save the presentation
    onProgress?.('Saving presentation...', totalSteps, totalSteps);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('.')[0];
    const fileName = `CHG_Finance_report_${timestamp}.pptx`;
    await pptx.writeFile({ fileName });
    console.log(`[chartExport] Successfully exported to ${fileName}`);
  } catch (error) {
    console.error('[chartExport] Error exporting to PowerPoint:', error);
    throw error;
  }
}

/**
 * Export all charts to PDF
 */
export async function exportChartsToPDF(
  charts: ChartData[],
  query?: string,
  insights?: AIInsight[],
  onProgress?: (step: string, current: number, total: number) => void
): Promise<void> {
  try {
    console.log(`[chartExport] Exporting ${charts.length} charts to PDF`);
    
    const totalSteps = charts.length + (insights && insights.length > 0 ? 2 : 1); // +1 for title, +1 for insights if present
    let currentStep = 0;
    
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const contentWidth = pageWidth - 2 * margin;
    const contentHeight = pageHeight - 2 * margin;

    // Page 1: Financial Data Overview
    onProgress?.('Creating title page...', ++currentStep, totalSteps);
    pdf.setFontSize(32);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(31, 58, 95); // #1F3A5F
    pdf.text('Financial Data Analysis', pageWidth / 2, margin + 20, { align: 'center' });
    
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(44, 122, 123); // #2C7A7B
    pdf.text('Comprehensive Financial Dashboard Report', pageWidth / 2, margin + 35, { align: 'center' });
    
    if (query) {
      pdf.setFontSize(12);
      pdf.setTextColor(102, 102, 102);
      pdf.setFont('helvetica', 'italic');
      const queryText = `Analysis Query: "${query}"`;
      pdf.text(queryText, pageWidth / 2, margin + 50, { align: 'center', maxWidth: contentWidth });
    }
    
    pdf.setFontSize(10);
    pdf.setTextColor(153, 153, 153);
    pdf.setFont('helvetica', 'normal');
    const dateText = `Generated on ${new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })}`;
    pdf.text(dateText, pageWidth / 2, margin + 65, { align: 'center' });
    
    pdf.setFontSize(11);
    pdf.setTextColor(102, 102, 102);
    pdf.text('This report contains comprehensive financial analysis including', pageWidth / 2, margin + 80, { align: 'center' });
    pdf.text('key insights, metrics, and visualizations of your financial data.', pageWidth / 2, margin + 88, { align: 'center' });

    // Page 2: Insights Summary
    if (insights && insights.length > 0) {
      onProgress?.('Adding insights summary...', ++currentStep, totalSteps);
      pdf.addPage();
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(31, 58, 95);
      pdf.text('Executive Summary & Key Insights', pageWidth / 2, margin + 15, { align: 'center' });
      
      let yPos = margin + 30;
      
      insights.forEach((insight, index) => {
        // Check if we need a new page before adding this insight
        if (yPos > pageHeight - margin - 20) {
          // Start a new page if we run out of space
          pdf.addPage();
          pdf.setFontSize(20);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(31, 58, 95);
          pdf.text('Executive Summary & Key Insights (Continued)', pageWidth / 2, margin + 15, { align: 'center' });
          yPos = margin + 30;
        }
        
        // Add bullet point with color based on type - centered
        const bulletColor = insight.type === 'warning' ? [245, 158, 11] : 
                          insight.type === 'success' ? [16, 185, 129] : 
                          insight.type === 'alert' ? [239, 68, 68] : [59, 130, 246];
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(bulletColor[0], bulletColor[1], bulletColor[2]);
        const titleText = `• ${insight.title}`;
        pdf.text(titleText, pageWidth / 2, yPos, { align: 'center' });
        
        // Add full description without truncation - centered and wrapped
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(85, 85, 85);
        // Split text to fit within content width (centered, use full width minus margins)
        const lines = pdf.splitTextToSize(insight.description, contentWidth - 20);
        
        // Calculate starting y position for centered multi-line text
        const lineHeight = 5;
        const totalTextHeight = lines.length * lineHeight;
        let textYPos = yPos + 6;
        
        // Draw each line centered
        lines.forEach((line: string, lineIndex: number) => {
          pdf.text(line, pageWidth / 2, textYPos + (lineIndex * lineHeight), { align: 'center' });
        });
        
        // Calculate height used by this insight
        const titleHeight = 6;
        const descriptionHeight = totalTextHeight;
        const spacing = 4;
        yPos += titleHeight + descriptionHeight + spacing;
      });
    }

    // Process each chart (starting from page 3)
    for (let i = 0; i < charts.length; i++) {
      const chart = charts[i];
      
      // Add new page for each chart
      pdf.addPage();
      onProgress?.(`Processing chart ${i + 1}/${charts.length}: ${chart.title}`, ++currentStep, totalSteps);

      console.log(`[chartExport] Processing chart ${i + 1}/${charts.length}: ${chart.title}`);

      // Find the chart element in the DOM
      const chartElement = document.querySelector(`[data-chart-id="chart-${i}"]`) as HTMLElement;
      
      if (!chartElement) {
        console.warn(`[chartExport] Chart element not found for: ${chart.title}`);
        // Add text instead
        pdf.setFontSize(20);
        pdf.text(chart.title, margin, margin + 10);
        pdf.setFontSize(12);
        pdf.setTextColor(150, 150, 150);
        pdf.text('Chart not available', margin, margin + 20);
        pdf.setTextColor(0, 0, 0);
        continue;
      }

      // Scroll element into view to ensure it's rendered
      chartElement.scrollIntoView({ behavior: 'instant', block: 'center' });
      await new Promise(resolve => setTimeout(resolve, 300));

      // Capture chart as image
      const canvas = await html2canvas(chartElement, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: false,
      });

      // Convert canvas to image data
      const imageData = canvas.toDataURL('image/png');

      // Add chart title
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text(chart.title, margin, margin + 8);

      // Calculate image dimensions to fit page
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;
      const maxHeight = contentHeight - 20; // Leave space for title and summary
      const finalHeight = Math.min(imgHeight, maxHeight);
      const finalWidth = (canvas.width * finalHeight) / canvas.height;

      // Center the image
      const xPos = (pageWidth - finalWidth) / 2;
      const yPos = margin + 15;

      // Add chart image
      pdf.addImage(imageData, 'PNG', xPos, yPos, finalWidth, finalHeight);

      // Add data summary
      if (chart.data && chart.data.length > 0) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        const summaryText = `Data Points: ${chart.data.length}`;
        const textWidth = pdf.getTextWidth(summaryText);
        pdf.text(summaryText, (pageWidth - textWidth) / 2, yPos + finalHeight + 5);
        pdf.setTextColor(0, 0, 0);
      }
    }

    // Save the PDF
    onProgress?.('Saving PDF document...', totalSteps, totalSteps);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('.')[0];
    const fileName = `CHG_Finance_report_${timestamp}.pdf`;
    pdf.save(fileName);
    console.log(`[chartExport] Successfully exported to ${fileName}`);
  } catch (error) {
    console.error('[chartExport] Error exporting to PDF:', error);
    throw error;
  }
}

